import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  // Phase A18-B1 — preferred-language persistence after login.
  // The Lang provider sits OUTSIDE the Auth provider in App.js, so it
  // is safe to subscribe to its callbacks here. `setLangAuto` is a
  // no-op when the user has already made a manual choice in this
  // session — see comments in `/app/frontend/src/i18n/index.jsx`.
  const { setLangAuto, setLangManual, clearLangSource } = useLang();

  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tsw_user") || "null"); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem("tsw_token") || null);
  const [loading, setLoading] = useState(false);

  const persist = useCallback((tok, usr) => {
    if (tok) { localStorage.setItem("tsw_token", tok); setToken(tok); }
    else { localStorage.removeItem("tsw_token"); setToken(null); }
    if (usr) { localStorage.setItem("tsw_user", JSON.stringify(usr)); setUser(usr); }
    else { localStorage.removeItem("tsw_user"); setUser(null); }
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return null;
    try {
      const { data } = await api.get("/auth/me");
      persist(token, data);
      // Phase A18-B1 — apply the stored preferred language on session
      // rehydration. Respects an existing manual choice (no-op).
      if (data?.language) setLangAuto(data.language);
      return data;
    } catch {
      persist(null, null);
      return null;
    }
  }, [token, persist, setLangAuto]);

  useEffect(() => { if (token && !user) refresh(); }, [token, user, refresh]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persist(data.token, data.user);
      // Phase A18-B1 — apply saved preference. setLangAuto is a no-op if
      // the user manually switched language during this browser session.
      if (data?.user?.language) setLangAuto(data.user.language);
      // EVH (Feb 2026) — surface the soft_login flag via a tiny window
      // global so the Login page can route to /verify-email-pending
      // without changing the return contract of login(). The flag is a
      // one-shot — read once by the caller and never persisted.
      try { window.__lastLoginSoft = data.soft_login === true; } catch {}
      return data.user;
    } finally { setLoading(false); }
  }, [persist, setLangAuto]);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      persist(data.token, data.user);
      // Phase A18-B1 — registration is the moment of explicit preference.
      // Treat it as a manual choice so the rest of the post-signup flow
      // (verify-email, dashboard, first questionnaire) renders in the
      // language the user actually picked on the form.
      if (data?.user?.language) setLangManual(data.user.language);
      try { window.__lastLoginSoft = data.soft_login === true; } catch {}
      return data;
    } finally { setLoading(false); }
  }, [persist, setLangManual]);

  const logout = useCallback(() => {
    // Phase A18-B1 — clear the manual/auto marker so the next user
    // signing in on this browser starts from a clean slate and gets
    // their own saved preference auto-applied.
    clearLangSource();
    persist(null, null);
  }, [persist, clearLangSource]);

  const setUserCb = useCallback((u) => persist(token, u), [token, persist]);

  // Memoize the context value so consumers don't re-render unless real state changes.
  const value = useMemo(
    () => ({ user, token, loading, login, register, logout, refresh, setUser: setUserCb }),
    [user, token, loading, login, register, logout, refresh, setUserCb]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
